import * as React from "react"

import { FormFieldShell, type FormFieldProps } from "@/components/forms/FormField"
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "cn"

type SelectRootProps = React.ComponentProps<typeof Select>

export type FormSelectProps = FormFieldProps &
  Omit<
    SelectRootProps,
    "children" | "onValueChange" | "value" | "defaultValue" | "multiple"
  > & {
    id?: string
    placeholder?: string
    children: React.ReactNode
    value?: string | null
    defaultValue?: string | null
    /**
     * Selected option value, or `null` when cleared. Matches Base UI / shadcn
     * Select (`onValueChange`) and {@link StringFormCombobox}, not native
     * input events.
     */
    onChange?: (value: string | null) => void
    /** Class names applied to the select trigger (the visible control). */
    inputClassName?: string
    /** Alias for select-specific trigger styling; merged with `inputClassName`. */
    triggerClassName?: string
    ref?: React.ComponentProps<typeof SelectTrigger>["ref"]
  }

/**
 * Labeled select with description / error associations and required marker.
 *
 * Drive with `value` / `onChange(value)` (react-hook-form `Controller` or
 * local state). Unlike {@link FormInput}, `onChange` receives the value
 * directly — same model as shadcn/Base UI Select and our combobox wrappers.
 * Pass `SelectItem` children for options.
 *
 * @example RHF
 * ```tsx
 * <Controller
 *   control={control}
 *   name="currency"
 *   render={({ field }) => (
 *     <FormSelect
 *       label="Currency"
 *       required
 *       placeholder="Select a currency"
 *       value={field.value || null}
 *       onChange={(value) => field.onChange(value ?? "")}
 *       error={errors.currency?.message}
 *     >
 *       <SelectItem value="USD">USD</SelectItem>
 *       <SelectItem value="GBP">GBP</SelectItem>
 *     </FormSelect>
 *   )}
 * />
 * ```
 *
 * @example Controlled (no RHF)
 * ```tsx
 * <FormSelect
 *   label="Currency"
 *   placeholder="Select a currency"
 *   value={currency}
 *   onChange={setCurrency}
 * >
 *   <SelectItem value="USD">USD</SelectItem>
 *   <SelectItem value="GBP">GBP</SelectItem>
 * </FormSelect>
 * ```
 */
export function FormSelect({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  triggerClassName,
  id,
  ref,
  disabled,
  placeholder,
  children,
  value,
  defaultValue,
  onChange,
  ...selectProps
}: FormSelectProps) {
  const generatedId = React.useId()
  const selectId = id ?? generatedId
  const invalid = Boolean(error)

  return (
    <FormFieldShell
      label={label}
      required={required}
      description={description}
      error={error}
      fieldClassName={fieldClassName}
      htmlFor={selectId}
      invalid={invalid}
      disabled={disabled}
    >
      {(controlProps) => (
        <Select
          {...selectProps}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          onValueChange={(next) =>
            onChange?.(next == null ? null : String(next))
          }
        >
          <SelectTrigger
            ref={ref}
            id={controlProps.id}
            aria-describedby={controlProps["aria-describedby"]}
            className={cn("w-full", inputClassName, triggerClassName)}
            aria-invalid={invalid || undefined}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent alignItemWithTrigger={false}>{children}</SelectContent>
        </Select>
      )}
    </FormFieldShell>
  )
}
